import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { loadGoogleMaps } from '@/lib/utils/maps';
import { VenueApiResult } from '../../../types/venue';
import VenueMarker from './VenueMarker';
import { getUserLocation } from '@/lib/utils/location';
import { Car, Bike, Footprints, Globe, LocateFixed } from 'lucide-react';

interface SimpleMapProps {
  venues?: VenueApiResult[];
  className?: string;
  onMarkerClick?: (venueId: string) => void;
  selectedVenueId?: string | null;
  userAvatarUrl?: string | null;
}

const SimpleMap: React.FC<SimpleMapProps> = ({
  venues = [],
  className,
  onMarkerClick,
  selectedVenueId,
  userAvatarUrl
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [transportMode, setTransportMode] = useState<'WALKING' | 'BICYCLING' | 'DRIVING'>('WALKING');
  const [mapType, setMapType] = useState<google.maps.MapTypeId>('satellite');

  useEffect(() => {
    if (mapReady && venues.length > 0 && !selectedVenueId && onMarkerClick) {
      onMarkerClick(venues[0].id);
    }
  }, [mapReady, venues, selectedVenueId, onMarkerClick]);

  useEffect(() => {
    let mounted = true;

    const initMap = async () => {
      if (!mapRef.current) return;
      try {
        await loadGoogleMaps();
        if (!mounted) return;

        const center = venues.length
          ? { lat: venues[0].coordinates.lat, lng: venues[0].coordinates.lng }
          : { lat: 0, lng: 0 };

        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 13,
          gestureHandling: 'greedy',
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          draggable: true,
          mapTypeId: mapType
        });

        overlayRef.current = new (class extends google.maps.OverlayView {
          div: HTMLDivElement | null = null;
          onAdd() {
            this.div = document.createElement('div');
            this.div.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
            this.div.style.position = 'absolute';
            this.div.style.top = '0';
            this.div.style.left = '0';
            this.div.style.width = '100%';
            this.div.style.height = '100%';
            this.div.style.pointerEvents = 'none';
            const panes = this.getPanes();
            if (panes?.overlayLayer) panes.overlayLayer.appendChild(this.div);
          }
          draw() {
            if (!this.div) return;
            const projection = this.getProjection();
            const bounds = this.getMap().getBounds();
            if (!bounds) return;
            const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
            const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
            this.div.style.left = sw.x + 'px';
            this.div.style.top = ne.y + 'px';
            this.div.style.width = (ne.x - sw.x) + 'px';
            this.div.style.height = (sw.y - ne.y) + 'px';
          }
          onRemove() {
            if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
            this.div = null;
          }
        })();

        overlayRef.current.setMap(map);
        mapInstanceRef.current = map;
        setMapReady(true);
      } catch (err) {
        console.error('Map init error:', err);
        setError('Failed to load map');
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        google.maps.event.clearInstanceListeners(mapInstanceRef.current);
        mapInstanceRef.current = null;
      }
      overlayRef.current?.setMap(null);
      userMarkerRef.current?.setMap(null);
      directionsRendererRef.current?.setMap(null);
    };
  }, [mapType]);

  useEffect(() => {
    if (!mapInstanceRef.current || !userAvatarUrl || !selectedVenueId) return;

    getUserLocation().then(location => {
      if (!location) return;
      const position = { lat: location.latitude, lng: location.longitude };
      setUserLocation(position);

      if (!userMarkerRef.current) {
        userMarkerRef.current = new google.maps.Marker({
          map: mapInstanceRef.current,
          position,
          icon: {
            url: userAvatarUrl,
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 18)
          },
          zIndex: 9999
        });
      } else {
        userMarkerRef.current.setPosition(position);
      }
    });
  }, [mapReady, userAvatarUrl, selectedVenueId]);

  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation || !selectedVenueId) return;
    const venue = venues.find(v => v.id === selectedVenueId);
    if (!venue) return;

    const directionsService = new google.maps.DirectionsService();
    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new google.maps.DirectionsRenderer({ suppressMarkers: true });
    }
    directionsRendererRef.current.setMap(mapInstanceRef.current);

    directionsService.route(
      {
        origin: userLocation,
        destination: venue.coordinates,
        travelMode: google.maps.TravelMode[transportMode]
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          directionsRendererRef.current?.setDirections(result);
          const bounds = new google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach(p => bounds.extend(p));
          mapInstanceRef.current?.fitBounds(bounds);
        }
      }
    );
  }, [userLocation, selectedVenueId, transportMode]);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !selectedVenueId || userAvatarUrl) return;
    const venue = venues.find(v => v.id === selectedVenueId);
    if (venue) {
      mapInstanceRef.current.setZoom(15);
      mapInstanceRef.current.panTo(venue.coordinates);
    }
  }, [mapReady, selectedVenueId, userAvatarUrl]);

  const centerOnUser = () => {
    if (!mapInstanceRef.current || !userLocation) return;
    mapInstanceRef.current.panTo(userLocation);
    mapInstanceRef.current.setZoom(15);
  };

  if (error) {
    return (
      <div className="bg-dark-300 rounded-xl p-6 text-center">
        <h3 className="text-lg font-medium text-white mb-2">Failed to Load Map</h3>
        <p className="text-dark-800">{error}</p>
      </div>
    );
  }

  return (
    <div className={cn('relative w-full h-full rounded-xl', className)}>
      <div
        id="no-swipe-zone"
        ref={mapRef}
        className="absolute inset-0"
        style={{ touchAction: 'none' }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      />

      {mapReady &&
        venues.map((venue) => (
          <VenueMarker
            key={venue.id}
            venue={venue}
            map={mapInstanceRef.current!}
            theme="dark"
            onMarkerClick={onMarkerClick}
            isSelected={venue.id === selectedVenueId}
          />
        ))}

      {/* Top-right control panel */}
      <div className="absolute top-3 right-3 z-50 flex gap-2 bg-black/60 p-2 rounded-lg shadow">
        <button onClick={() => setTransportMode('DRIVING')} style={{ color: '#fff' }} className="p-2 rounded hover:bg-white/10">
          <Car size={18} />
        </button>
        <button onClick={() => setTransportMode('WALKING')} style={{ color: '#fff' }} className="p-2 rounded hover:bg-white/10">
          <Footprints size={18} />
        </button>
        <button onClick={() => setTransportMode('BICYCLING')} style={{ color: '#fff' }} className="p-2 rounded hover:bg-white/10">
          <Bike size={18} />
        </button>
        <button onClick={() => {
          const next = mapType === 'satellite' ? 'roadmap' : 'satellite';
          setMapType(next);
          mapInstanceRef.current?.setMapTypeId(next);
        }} style={{ color: '#fff' }} className="p-2 rounded hover:bg-white/10">
          <Globe size={18} />
        </button>
      </div>

      {/* Bottom-left "Locate Me" */}
      {userLocation && (
        <div className="absolute bottom-20 left-3 z-50">
          <button
            onClick={centerOnUser}
            className="p-2 rounded-full bg-black/60 hover:bg-white/10 shadow"
            style={{ color: '#fff' }}
          >
            <LocateFixed size={18} />
          </button>
        </div>
      )}
      <div ref={mapContainerRef} className={cn("h-96 w-full rounded-lg overflow-hidden -mx-0 sm:-mx-0 md:-mx-0 lg:-mx-0", className)} />
      <div ref={mapContainerRef} className={cn("h-96 w-full rounded-lg overflow-hidden", className)} />
    </div>
  );
};

export default SimpleMap;
